import { useCallback, useEffect, useState } from "react";

import { csrfHeaders } from "../../../utils/csrf";

// Plain-fetch API client for the verify-account endpoints (TIP-453).
// Self-contained inside this subtree — no Redux store, no RTK Query —
// mirroring the AgentIntake pattern. Auth = the activation_token in the
// URL; no JWT / session needed.

const API_BASE = "/api/lead-activation/lead";

// TIP-535 (VAPT #1/#2) — the backend now demands a signed gate token
// (issued after the nominee verifies an email OTP) on every lead endpoint,
// sent as the X-Verify-Session header. We keep it in sessionStorage keyed
// by the lead token so it survives the Stripe Checkout redirect round-trip
// but dies with the tab.
const GATE_STORAGE_PREFIX = "trevex.verify-gate.";

export function readGateToken(token) {
  try {
    return sessionStorage.getItem(GATE_STORAGE_PREFIX + token) || null;
  } catch {
    return null;
  }
}

function storeGateToken(token, gateToken) {
  try {
    if (gateToken) sessionStorage.setItem(GATE_STORAGE_PREFIX + token, gateToken);
  } catch {}
}

export function clearGateToken(token) {
  try {
    sessionStorage.removeItem(GATE_STORAGE_PREFIX + token);
  } catch {}
}

async function apiRequest(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const headers = isFormData
    ? options.headers || {}
    : { "Content-Type": "application/json", ...(options.headers || {}) };

  // Every path in this client starts with /<lead-token>/ — attach the gate
  // token for that lead when we hold one (harmless on the gate endpoints).
  const leadToken = path.split("/").filter(Boolean)[0];
  const gateToken = leadToken ? readGateToken(leadToken) : null;
  if (gateToken) headers["X-Verify-Session"] = gateToken;

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
    ...options,
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    /* no body */
  }

  if (!response.ok) {
    // A 401 otp_required means our gate token is missing/expired/wrong —
    // drop the stale one so the UI re-challenges instead of looping.
    if (response.status === 401 && data?.code === "otp_required" && leadToken) {
      clearGateToken(leadToken);
    }
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Gate — step 1: email a 6-digit code to the nominee's address on file.
// Response carries only the masked email + expiry (nothing else pre-gate).
export async function requestGateOtp(token) {
  return apiRequest(`/${token}/gate/request/`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// Gate — step 2: exchange the code for the signed gate token. On success
// the token is persisted for this lead and the full lead payload returns.
export async function verifyGateOtp(token, otp) {
  const result = await apiRequest(`/${token}/gate/verify/`, {
    method: "POST",
    body: JSON.stringify({ otp }),
  });
  if (result?.gate_token) storeGateToken(token, result.gate_token);
  return result;
}

export function useVerifyLead(token, options = {}) {
  // `skip` lets callers (e.g. the Stripe-Checkout return flow at
  // VerifyAccountPage) bypass the lead GET entirely so it doesn't 410
  // with token_used when we're already in the post-activate state.
  const { skip = false } = options;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(token) && !skip);

  const refetch = useCallback(async () => {
    if (!token || skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest(`/${token}/`);
      setData(result);
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, skip]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, error, loading, refetch, setData, setError };
}

// PATCH a single section. Body shape per TIP-453's verify_views:
export async function patchVerifyLead(token, body) {
  return apiRequest(`/${token}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// Final activate — atomic User + Company + IndComp + auto-approved
// CompanyAdminRequests create flow. Backend rejects if all 6 confirms
// aren't true or password fails Django's validators.
export async function activateLead(token, password) {
  return apiRequest(`/${token}/activate/`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// TIP-455 — post-activation Pay/Skip. Body shape:
// Pay provisions VerifiedCompany + a placeholder VerifyCompanyPayment row;
// Skip just flips Company.subscription_status. Idempotent on token.
// (Note: the pay path is dead code once TIP-464 ships — PaymentScreen
// now goes via Stripe Embedded Checkout. Kept for skip-path use only.)
export async function finaliseLead(token, body) {
  return apiRequest(`/${token}/finalise/`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// TIP-464 — read-only snapshot of the post-activate state. Used by
// VerifyAccountPage when it detects ?stripe=cs_... in the URL after a
// Stripe Checkout redirect. Returns: { status, user, company } where
// company.subscription_status is 'accessed' until the webhook lands +
// flips it to 'verified_paid'. Caller polls until it transitions.
export async function fetchPostActivateState(token) {
  return apiRequest(`/${token}/post-activate-state/`, { method: 'GET' });
}

// TIP-464 — create a Stripe Embedded Checkout Session for the Verified
// subscription. Different endpoint family (/api/billing/) so this can't
// use the apiRequest helper (which prepends /api/lead-activation/lead).
// Body: { token, plan: 'monthly' | 'yearly' }. Returns { client_secret }.
// stayOnPage (TIP-615) — set by callers that mount the returned secret in a
// modal and must not be navigated away from. It makes the backend create the
// session with `redirect_on_completion: 'never'` and no return_url, which is
// what makes StripeCheckoutPanel's onComplete fire instead of Stripe
// redirecting the whole window. Omitted, the session keeps its return_url and
// the existing redirect flows are unchanged.
export async function createVerifiedCheckoutSession({
  companyID,
  token,
  plan,
  stayOnPage = false,
}) {
  const body = {
    plan,
    ...(companyID && { company_id: companyID }),
    ...(token && { token }),
    ...(stayOnPage && { stay_on_page: true }),
  };

  // The CSRF header, which this raw fetch has to add for itself.
  // The rest of the app changes state through RTK Query, whose baseQuery sets
  // X-CSRFToken in prepareHeaders. This call predates the cookie session and
  // never went through it: `credentials: 'include'` sends the session cookie,
  // so CustomJWTAuthentication authenticates FROM THE COOKIE and then demands
  // the double-submit header — which was never sent. Every membership
  // checkout 403'd with "CSRF verification failed" before the Stripe session
  // was created. Safe methods do not need it; this one is a POST.
  const response = await fetch('/api/billing/verified-checkout/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await response.json(); } catch { /* no body */ }
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// TIP-650 — /membership/:companyId arrives with only the id, so PaymentScreen
// looks the company up itself: the name for its hero, and `claimed` to decide
// between the checkout and the claim-first CTA. Same public endpoint the
// company profile page reads (anonymous callers get the basic serializer, which
// still carries both). Returns { name, claimed } — name is null when none is on
// file; throws with `status` when the id doesn't resolve.
export async function fetchCompanySummary(companyID) {
  const response = await fetch(
    `/api/company/company-details/${encodeURIComponent(companyID)}`,
    { credentials: "include", headers: { Accept: "application/json" } }
  );
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  return {
    name: data?.registered_company_name?.trim() || null,
    claimed: Boolean(data?.claimed),
  };
}

// Nominee — edit name / designation / phone. Email is OTP-gated below.
export async function patchNominee(token, body) {
  return apiRequest(`/${token}/nominee/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// Email change — step 1: request OTP delivered to the new email.
export async function requestEmailOtp(token, newEmail) {
  return apiRequest(`/${token}/nominee/email-otp/request/`, {
    method: "POST",
    body: JSON.stringify({ new_email: newEmail }),
  });
}

// Email change — step 2: verify OTP; on success the backend swaps
// lead.nominee_email and returns the updated lead.
export async function verifyEmailOtp(token, otp) {
  return apiRequest(`/${token}/nominee/email-otp/verify/`, {
    method: "POST",
    body: JSON.stringify({ otp }),
  });
}

// Lightweight reference data — kept here so the subtree owns its own
// API surface end-to-end without reaching into shared utils.
export async function fetchSubIndustries() {
  const res = await fetch(`/api/company/sub-industry/`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Sub-industry fetch failed: ${res.status}`);
  const json = await res.json();
  return json?.data || [];
}
